import React from "react";
import styled from "styled-components";
import Head from "next/head";
import Image from "next/image";
import { EventJsonLd } from "next-seo";
import { FaClock, FaMapMarkerAlt } from "react-icons/fa";
import * as Router from "next/router";
import * as Sentry from "@sentry/browser";
import * as R from "ramda";
import Player from "@vimeo/player";
import dayjs from "dayjs";
import axios from "axios";

import { ALLBRIGHT_URL } from "../../config.client";
import firebase from "../../firebase.client";
import * as c from "../../constants";
import * as gtag from "../../utils/gtag";
import * as u from "../../utils";
import * as h from "../../utils/hooks";
import * as T from "../../components/Typography";
import * as I from "../../components/Icons";
import * as UserContext from "../../components/UserContext";
import * as formatDateAndTime from "../../utils/formatDateAndTime";
import makeEventCals from "../../utils/makeCalendarUrls";
import Link from "../../components/Link";
import EventCollection from "../../components/EventCollection";
import EventCard from "../../components/EventCard";
import BackButton from "../../components/BackButton";
import Button from "../../components/Button";
import Select from "../../components/Select";
import WhatsYourMind from "../../components/WhatsYourMind";
import ActivityPost from "../../components/ActivityPost";
import UpsellModal from "../../components/UpsellModal";
import MembersCarousel from "../../components/MembersCarousel";
import { apiGetEventById } from "../api/event/get-by-id";
import Modal from "@/components/Modal";
import MarketingLink from "@/components/marketing/MarketingLink";

export default function Event(props) {
  const userInfo = React.useContext(UserContext.Context);
  const { hasLoaded } = userInfo;
  const router = Router.useRouter();
  const [loading, setLoading] = React.useState(false);
  const [others, setOthers] = React.useState();
  const [attendance, setAttendance] = React.useState();
  const [guestsCount, setGuestsCount] = React.useState(0);
  const [modalType, setModalType] = React.useState(false);
  const [rsvpError, setRsvpError] = React.useState();
  const [refundError, setRefundError] = React.useState(false);
  const [currentVideoRef, setCurrentVideoRef] = React.useState();
  const [liveClassModalOpen, setLiveClassModalOpen] = React.useState(false);
  const isDigital = props.event.isDigitalOrPhysical === "DIGITAL EVENT";
  const calLinks = makeEventCals(props.event, isDigital);
  const forTotal = (total, attendee) => total + 1 + (+attendee.guests || 0);
  const totalAttendees = props.event.attendees?.reduce(forTotal, 0);
  const isLoggedIn = !!userInfo.id;
  const isPastEvent = props.event.endTime < dayjs().unix();
  const isRecordedEvent = props.event.recordedEventVideoUrl;
  const isLocked = !isAvailableToMe();
  const isBookmark = isRecordedEvent || isPastEvent;
  const isWithinTwoDays = props.event.startTime - dayjs().unix() < 86400 * 2;
  const isFullEvent =
    props.event.ticketCapacity && totalAttendees >= props.event.ticketCapacity;

  const maybeAdditionalCost = +props.event.clubPlusAmount || 0;
  const maybeAdditionalGuests = +props.event.clubPlusGuests || 0;
  const showTicketPrice = maybeAdditionalCost > 0;
  const ticketPrice = maybeAdditionalCost.toFixed(2);
  const showAllbrightPlusDiscountedTicketPrice =
    maybeAdditionalCost && +props.event.plusDiscount > 0;
  const allbrightPlusDiscountedTicketPrice = (
    maybeAdditionalCost *
    (1 - props.event.plusDiscount)
  ).toFixed(2);
  const showGuestSelect =
    maybeAdditionalGuests > 0 && !isPastEvent && !isFullEvent;
  const isIncludedInMembership = props.event.isFreeForMember && userInfo.isPaid;
  const isExclusive = !!props.event.exclusiveGroups?.length;

  const checkIfLiveClass = (url) => {
    if (url?.includes("webinar") || url?.includes("everywoman")) return true;
    else return false;
  };

  const isLiveClass = checkIfLiveClass(props?.event?.linkUrl);

  React.useEffect(checkExclusivity, []);
  React.useEffect(() => {
    readAttendees();
  }, [isLoggedIn]);
  React.useEffect(() => {
    getVideo();
  }, [currentVideoRef]);

  const onRefChange = React.useCallback(
    (ref) => {
      setCurrentVideoRef(ref);
    },
    [currentVideoRef]
  );

  if (!isLoggedIn && isExclusive) return null;

  return (
    <S.Container data-cy="event-container">
      {getHead()}
      <EventJsonLd {...getJsonLdProps()} />
      {hasLoaded && (
        <>
          {liveClassModalOpen && (
            <>
              <Modal
                onClose={() => setLiveClassModalOpen(false)}
                colour={u.colours.shellHighlight}
              >
                <S.ModalContent>
                  <S.ModalCopy>
                    <T.CXXL weight="700" colour={u.colours.textTurquoise}>
                      Register for this live class
                    </T.CXXL>
                    <T.CL>
                      Follow the link below to register for{" "}
                      <b>{props.event.title}</b>. You will then receive a
                      confirmation email securing your place, followed by
                      another email with the Zoom link 24 hours before the event
                      is due to start.
                    </T.CL>
                    <MarketingLink
                      href={props.event.linkUrl}
                      linkText="Register now"
                      linkTarget="_blank"
                      animationColour={u.colours.textTurquoise}
                    />
                  </S.ModalCopy>
                </S.ModalContent>
              </Modal>
            </>
          )}
          <UpsellModal type={modalType} setType={setModalType} />
          <S.Back>
            <BackButton text="Go to Events" backTo="/events" />
          </S.Back>
          <S.Event>
            {videoOrHero()}
            <S.EventContainer>
              <S.Header>
                <S.Details data-cy="event-details">
                  {logistics()}
                  {rsvp()}
                  {rsvpError && (
                    <TT.CL isError data-cy="rsvp-error">
                      {rsvpError}
                    </TT.CL>
                  )}
                </S.Details>
              </S.Header>
              <S.Content>
                <S.Name>
                  <TT.H3>{props.event.title}</TT.H3>
                </S.Name>
                <S.Info data-cy="event-info">
                  {props.event.description.split("\n").map((p) => (
                    <TT.CLA key={p} dangerouslySetInnerHTML={{ __html: p }} />
                  ))}
                </S.Info>
              </S.Content>
            </S.EventContainer>
          </S.Event>
          {!!others?.length && (
            <MembersCarousel
              members={others}
              datacy="members-attending"
              title="Other Attendees"
            />
          )}
          <Comments />
          <T.H4>More Events</T.H4>
          <EventCollection>
            {getUpcomingEvents(props.events).map(getEvent)}
          </EventCollection>
          <S.Button isSecondary onClick={() => router.push("/events")}>
            View all events
          </S.Button>
        </>
      )}
    </S.Container>
  );

  function isAvailableToMe() {
    if (isPastEvent) return userInfo.isPaid;
    if (props.event.isFree) return isLoggedIn;

    switch (props.event.memberAccess) {
      case "REGISTERED":
        return isLoggedIn;
      case "CLUB":
        return !!userInfo.isClub;
      default:
        return !!userInfo.isPaid;
    }
  }

  function checkExclusivity() {
    const userHasAccess = props.event.exclusiveGroups?.some((groupId) =>
      userInfo?.groups?.includes(groupId)
    );
    if (isExclusive && !userHasAccess) router?.replace("/events");
  }

  async function readAttendees() {
    if (!isLoggedIn) return;

    const db = firebase.firestore();
    const eventRef = db.collection("events");
    const usersRef = db.collection("users");
    const event = (await eventRef.doc(props.event.id).get()).data();
    if (!event?.attendees) return setAttendance(null);
    const isThisUser = (a) => [a, a?.id].includes(userInfo.id);
    const myAttendance = event.attendees.find(isThisUser);
    if (myAttendance?.id) setGuestsCount(myAttendance.guests || 0);
    setAttendance(myAttendance || null);
    setLoading(false);

    const attendeeDocs = event.attendees
      .map((user) => user?.id && usersRef.doc(user.id).get())
      .filter(Boolean);
    const otherAttendeeUsers = (await Promise.all(attendeeDocs))
      .map((doc) => doc.data())
      .filter((a) => !isThisUser(a));
    setOthers(otherAttendeeUsers);
  }

  function getVideo() {
    if (!currentVideoRef) return;
    if (!isLocked && props.event.recordedEventVideoUrl) {
      new Player(currentVideoRef, {
        url: props.event.recordedEventVideoUrl.trim(),
      });
    }
  }

  function videoOrHero() {
    if (!isLocked && props.event.recordedEventVideoUrl)
      return <S.Video ref={onRefChange} />;

    if (!isLoggedIn && props.event.recordedEventVideoUrl) {
      return (
        <Placeholder {...props}>
          <TT.CLC>Sign up or log in to watch our on-demand events.</TT.CLC>
          <Button
            onClick={() => {
              setModalType("play");
            }}
          >
            Sign up <S.Lock />
          </Button>
        </Placeholder>
      );
    }

    if (isLocked && props.event.recordedEventVideoUrl) {
      return (
        <Placeholder {...props}>
          <TT.CLC>
            Upgrade your AllBright Digital membership to access this course.
          </TT.CLC>
          <Link href={"/digital/select-plan"}>
            <Button>
              Upgrade to unlock <S.Lock />
            </Button>
          </Link>
        </Placeholder>
      );
    }

    return (
      <S.Image data-cy="event-image">
        <S.BgImage
          loader={() => props.event.heroImage}
          src={props.event.heroImage}
          alt={`Attend ${props.event.title}`}
          layout="fill"
          objectFit="cover"
          unoptimized
        />
        <Image
          loader={() => props.event.heroImage}
          src={props.event.heroImage}
          alt={`Attend ${props.event.title}`}
          layout="fill"
          objectFit="contain"
          unoptimized
        />
      </S.Image>
    );

    function Placeholder(props) {
      return (
        <S.ImageWrapper>
          <Image
            loader={() => props.event.heroImage}
            src={props.event.heroImage}
            alt={"Attend " + props.event.title}
            layout="fill"
            objectFit="cover"
          />
          <S.Overlay>{props.children}</S.Overlay>
        </S.ImageWrapper>
      );
    }
  }

  function logistics() {
    const maxGuests = Math.max(props.event.ticketCapacity - totalAttendees, 0);
    const totalSpaces = maybeAdditionalGuests + 1;
    const optsCount = props.event.ticketCapacity ? maxGuests : totalSpaces;
    const guestsOpts = [...Array(totalSpaces).keys()].slice(0, optsCount);

    return (
      <>
        <TT.CS>TIME & DATE</TT.CS>
        <TT.CMA>{formatDateAndTime.format(props.event.startTime)}</TT.CMA>
        <TT.CS>Duration</TT.CS>
        <S.Duration>
          <S.Clock />
          {props.event.duration > 0 && (
            <TT.CMA>
              {formatDateAndTime.convertMinutesToHours(props.event.duration)}
            </TT.CMA>
          )}
        </S.Duration>
        <TT.CS>LOCATION</TT.CS>
        <S.Location>
          <S.MapIcon />
          <T.CL>{props.event.location}</T.CL>
          {attendance && isDigital && !isLiveClass && (
            <Link href={`/events/join?id=${props.event.id}`} isTextAnimation>
              Joining details
            </Link>
          )}
        </S.Location>
        {showTicketPrice && (
          <>
            {showAllbrightPlusDiscountedTicketPrice ? (
              <>
                <TT.CS>ALLBRIGHT DIGITAL MEMBER TICKET PRICE</TT.CS>
                <TT.CMA>£{allbrightPlusDiscountedTicketPrice}</TT.CMA>

                <TT.CS>NON-MEMBER TICKET PRICE</TT.CS>
                <TT.CMA>£{ticketPrice}</TT.CMA>
              </>
            ) : (
              <>
                <TT.CS>TICKET PRICE</TT.CS>
                <TT.CMA>£{ticketPrice}</TT.CMA>
              </>
            )}
          </>
        )}
        {showGuestSelect && !isDigital && (
          <Select
            name="guests"
            placeholder="Number of guests (0)"
            value={guestsCount}
            onSelect={setGuestsCount}
            opts={guestsOpts}
            shouldScroll
            isDisabled={!!attendance}
          />
        )}
      </>
    );
  }

  function rsvp() {
    if (!isLoggedIn) {
      return (
        <>
          <Button
            isWide
            onClick={() => setModalType("rsvp")}
            text="Join event"
          />
          <ShareOptions />
        </>
      );
    }

    if (isLocked) {
      u.sessionStorage.setItem(c.RETURN_TO, `/events/${props.event.id}`);
      const upgradeType = isDigital ? "digital" : "club";

      return (
        <Button
          onClick={() => setModalType(upgradeType)}
          isWide
          showIcon
          text="Upgrade to Attend"
        />
      );
    }

    if (props.event.peopleVineUrl) {
      return (
        <Button
          isWide
          showIcon
          text="Join event"
          onClick={() =>
            window.open(props.event.peopleVineUrl, "_blank").focus()
          }
        />
      );
    }

    if (attendance) {
      return (
        <>
          <Button
            isWide
            text={isBookmark ? "Remove Bookmark" : "Leave event"}
            onClick={onLeaveRsvp}
            isLoading={loading}
            isSecondary
            isDisable={
              !isBookmark &&
              showTicketPrice &&
              isWithinTwoDays &&
              !isIncludedInMembership
            }
          />
          {calLinks && (
            <>
              <TT.CMC>Add to calendar:</TT.CMC>
              <S.Row>
                <TT.CMC>
                  <T.A
                    href={calLinks.google}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Google
                  </T.A>
                </TT.CMC>
                <TT.CMC>
                  <T.A href={calLinks.ics}>iCal</T.A>
                </TT.CMC>
                <TT.CMC>
                  <T.A
                    href={calLinks.yahoo}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Yahoo
                  </T.A>
                </TT.CMC>
              </S.Row>
            </>
          )}
          <ShareOptions />
          {showTicketPrice && (
            <>
              {refundError && (
                <TT.CL isError data-cy="refund-error">
                  There has been an issue with your refund, please contact us
                  for assistance.
                </TT.CL>
              )}
              <TT.CL>
                If you can no longer make it, you can get your refund by
                clicking the button above.
              </TT.CL>
              <TT.CL>
                Note that we will not refund your ticket(s) after{" "}
                {dayjs
                  .unix(props.event.startTime)
                  .subtract(2, "day")
                  .format("HH:mma [on] dddd DD MMMM YYYY")}
                .
              </TT.CL>
              <TT.CL>
                <T.A href="/terms-conditions">
                  Read our full terms and conditions
                </T.A>
                .
              </TT.CL>
            </>
          )}
          {props.event.confirmByEmail && (
            <TT.CL>
              A member of the AllBright team will be in contact for more details
              about your booking.
            </TT.CL>
          )}
        </>
      );
    }
    function ShareOptions() {
      const pageLink = `${ALLBRIGHT_URL}${router.asPath}`;
      const [hasCopied, onCopy] = h.onCopyText(pageLink);

      return (
        <S.ShareOptionsWrap>
          <TT.CMC>
            {attendance ? "Invite your friends" : "Share this event"}
          </TT.CMC>
          <S.IconWrap>
            <S.Twitter
              grow={1}
              onClick={() => share("tw")}
              data-cy="twitter-share"
            />
            <S.Facebook
              grow={1}
              onClick={() => share("fb")}
              data-cy="facebook-share"
            />
            <S.LinkedIn
              grow={1}
              onClick={() => share("li")}
              data-cy="linkedIn-share"
            />
            {hasCopied ? (
              <I.Tick width={"20px"} height={"20px"} />
            ) : (
              <S.Hyperlink onClick={onClick} width={"20px"} height={"20px"} />
            )}
          </S.IconWrap>
        </S.ShareOptionsWrap>
      );

      function onClick() {
        gtag.event({
          name: gtag.GA_EVENT.EVENT_ENGAGEMENT,
          category: "Events",
          label: "Copy event link",
          value: props.event.title,
        });

        onCopy();
      }
      function share(platform) {
        const launchPopup = (link) =>
          window.open(
            link,
            "share",
            `status=no,location=no,toolbar=no,menubar=no,width=600,height=600,left=100,top=100`
          );
        switch (platform) {
          case "fb":
            return launchPopup(
              `https://www.facebook.com/sharer/sharer.php?u=${pageLink}`
            );
          case "li":
            return launchPopup(
              `https://www.linkedin.com/sharing/share-offsite/?url=${pageLink}`
            );
          case "tw":
            return launchPopup(
              `https://twitter.com/intent/tweet?url=${pageLink}&text=${props.event.title}`
            );
        }
      }
    }

    if (isFullEvent) {
      return (
        <Button isSecondary isWide isDisable>
          Event full <S.Lock />
        </Button>
      );
    }

    if ((isIncludedInMembership || !showTicketPrice) && isLiveClass) {
      return (
        <>
          <Button
            onClick={() => {
              setLiveClassModalOpen(true);
            }}
            isWide
            text={isBookmark ? "Bookmark" : "Register"}
            isLoading={loading}
            datacy="events__rsvp--join"
          />
          <ShareOptions />
        </>
      );
    }

    if (isIncludedInMembership || !showTicketPrice) {
      return (
        <>
          <Button
            onClick={onEnterRsvp}
            isWide
            text={isBookmark ? "Bookmark" : "Join event"}
            isLoading={loading}
            datacy="events__rsvp--join"
          />
          <ShareOptions />
        </>
      );
    }

    const spacesCount = props.event.ticketCapacity - totalAttendees;
    const remainingTickets =
      spacesCount === 1
        ? `there is ${spacesCount} ticket left`
        : `there are ${spacesCount} tickets left`;
    const tickets = guestsCount ? `${guestsCount + 1} tickets` : "ticket";
    const forPrice = showAllbrightPlusDiscountedTicketPrice
      ? ` for £${userInfo.isPaid ? allbrightPlusDiscountedTicketPrice : ticketPrice}`
      : "";
    return (
      <>
        <Button
          isWide
          onClick={onBuyRsvp}
          text={isBookmark ? "Bookmark" : `Buy ${tickets}${forPrice}`}
          isLoading={loading}
        />
        {spacesCount < 3 && (
          <TT.CLA>
            This event is selling fast - {remainingTickets}. If all spaces are
            filled by the time your transaction goes through, we will process
            you a refund automatically and confirm this by email.
          </TT.CLA>
        )}
      </>
    );
  }

  async function onEnterRsvp() {
    setLoading(true);
    setRsvpError();

    try {
      const url = "/api/event.join";
      const body = { eventId: props.event.id, guestsCount };
      await axios.post(url, body);

      gtag.event({
        name: gtag.GA_EVENT.EVENT_ENGAGEMENT,
        category: "Events",
        label: "RSVP to " + props.event.isDigitalOrPhysical,
        value: +props.event.clubPlusAmount,
      });
    } catch (err) {
      Sentry.captureException(err);
      if (err?.response?.status === 403) {
        setRsvpError("It is not possible to join this event.");
      } else {
        setRsvpError(
          "There was a problem joining this event, please contact us for assistance."
        );
      }
      return setLoading(false);
    }

    await readAttendees();
  }

  async function onBuyRsvp() {
    setLoading(true);
    setRsvpError();

    const url = `/api/event.payment`;
    const body = { eventId: props.event.id, guestsCount, userId: userInfo.id };

    try {
      const res = await axios.post(url, body);

      gtag.event({
        name: gtag.GA_EVENT.EVENT_ENGAGEMENT,
        category: "Events",
        label: "Intention to pay for " + props.event.isDigitalOrPhysical,
        value: parseInt(props.event.clubPlusAmount),
      });

      return router.push(res.data);
    } catch (err) {
      Sentry.captureException(err);
      if (err?.response?.status === 403) {
        setRsvpError("It is not possible to join this event.");
      } else {
        setRsvpError(
          "There was a problem joining this event, please contact us for assistance."
        );
      }
      return setLoading(false);
    }
  }

  async function onLeaveRsvp() {
    setLoading(true);

    gtag.event({
      name: gtag.GA_EVENT.EVENT_ENGAGEMENT,
      category: "Events",
      label: "Leave " + props.event.isDigitalOrPhysical,
      value: +props.event.clubPlusAmount,
    });

    const body = { eventId: props.event.id, userId: userInfo.id };
    const hasPaid = !!attendance?.paymentId;

    if (!hasPaid) {
      try {
        await axios.post("/api/event.leave", body);
      } catch (err) {
        Sentry.captureException(err);
      }
      return await readAttendees();
    }

    if (isWithinTwoDays) return setLoading(false);

    try {
      await axios.post("/api/event.refund", body);
      setAttendance(null);
    } catch (err) {
      Sentry.captureException(err);
      setRefundError(true);
    }

    return setLoading(false);
  }

  function getUpcomingEvents(events) {
    const isFuture = (e) => e.endTime >= dayjs().unix();
    const oldFirst = (a, b) => a.startTime - b.startTime;

    return R.pipe(R.filter(isFuture), R.sort(oldFirst))([...events]);
  }

  function getEvent(props, index) {
    const isExclusive = !!props.exclusiveGroups?.length;
    const userHasAccess = props.exclusiveGroups?.some((group) =>
      userInfo.groups?.includes(group)
    );
    if (isExclusive && !userHasAccess) return null;

    return (
      <EventCard
        key={props.id}
        index={index}
        {...props}
        replaceHistory={true}
      />
    );
  }

  function Comments() {
    const { store, onCreatePost } = h.useStreamFeed("public", props.event.id);
    const comments = store || props.comments;

    return (
      <S.Comments>
        <TT.H3Rox>Comments</TT.H3Rox>
        <div onClick={() => !userInfo.id && setModalType("comment")}>
          <WhatsYourMind
            onCreatePost={onCreatePost}
            location="Events"
            placeholder="What do you think?"
          />
        </div>
        {comments.results?.map(getComment)}
      </S.Comments>
    );

    function getComment(elm) {
      const postProps = {
        key: elm.id,
        type: "comment",
        link: router.asPath,
        pageId: props.event.id,
        ...elm,
      };

      if (userInfo.id) return <ActivityPost {...postProps} />;

      return (
        <div key={elm.id} onClick={() => setModalType("comment")}>
          <ActivityPost {...elm} />
        </div>
      );
    }
  }

  function getHead() {
    const title = u.trimStr(props.event.title, 35) + " | Events | AllBright";
    const description =
      props.event.description.split(".")[0] +
      "." +
      props.event.description.split(".")[1];
    const info = {
      title,
      description,
      imgUrl: props.event.heroImage,
      imgAlt: props.event.title,
      url: ALLBRIGHT_URL + router.asPath,
    };

    const metaTags = [
      ...u.generateMetaTags(info),
      ...u.generateMetaType("event"),
    ];

    return (
      <Head>
        <title>{title}</title>
        {metaTags.map((elm) => (
          <meta {...elm} key={elm.name || elm.itemprop || elm.property} />
        ))}
      </Head>
    );
  }

  function getJsonLdProps() {
    return {
      name: props.event.title,
      startDate: dayjs
        .unix(props.event.startTime)
        .format("YYYY-MM-DDTHH:mm:ssZ[Z]"),
      endDate: dayjs
        .unix(props.event.endTime)
        .format("YYYY-MM-DDTHH:mm:ssZ[Z]"),
      location: {
        name: "The AllBright Mayfair",
        address: {
          streetAddress: "24-26 Maddox Street",
          addressLocality: "Mayfair",
          addressRegion: "London",
          postalCode: "W1S 1PP",
          addressCountry: "UK",
        },
      },
      url: props.event.linkUrl || "",
      images: [props.event.heroImage + "?w=350"],
      description:
        props.event.description?.split(".")[0] +
        "." +
        props.event.description?.split(".")[1],
    };
  }
}

export async function getStaticPaths() {
  return { paths: [], fallback: "blocking" };
}

export async function getStaticProps({ params }) {
  const firebase = require("../../firebase.admin");
  const config = require("../../config");
  const stream = require("getstream");
  const dayjs = require("dayjs");
  const today = dayjs().unix();

  try {
    const event = await apiGetEventById(params.event);

    const eventsRef = firebase
      .firestore()
      .collection("events")
      .where("status", "==", "LIVE")
      .where("endTime", ">=", today)
      .orderBy("endTime", "asc")
      .limit(5);
    const allEvents = (await eventsRef.get()).docs.map((doc) => doc.data());
    const filteredEvents = allEvents.filter(
      (e) =>
        e.id !== params.event &&
        (!e.exclusiveGroups || e.exclusiveGroups?.length === 0)
    );
    const events = filteredEvents.slice(0, 2);

    const streamClient = stream.connect(
      config.STREAM_PUBLIC,
      config.STREAM_SECRET,
      config.STREAM_APP_ID
    );
    const baseOpts = { limit: 30, reactions: { recent: true, counts: true } };

    const comments = await streamClient
      ?.feed("public", params.event)
      .get(baseOpts);
    const props = { event, events, comments };

    return { props, revalidate: 3600 };
  } catch (err) {
    console.error("✘ event error", err);

    return { notFound: true };
  }
}

const S = {};

S.Container = styled.div`
  max-width: 1440px;
  margin: auto;
  padding: 15px 16px 100px;
  @media ${u.device.mobile} {
    padding: 20px 20px 100px;
  }
  @media ${u.device.tablet} {
    padding-left: 40px;
    padding-bottom: 100px;
  }
`;

S.Back = styled.div`
  margin-bottom: 25px;
`;

S.Event = styled.article`
  margin: 0 auto;
  width: 100%;
  border-radius: 0 0 3px 3px;
  border: 1px solid ${u.colours.shellOverlay};
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  margin-bottom: 40px;
`;

S.ImageWrapper = styled.div`
  position: relative;
  flex: 1;
  padding-top: 30%;
`;

S.Overlay = styled.div`
  background: rgba(0, 0, 0, 0.5);
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
`;

S.Lock = styled(I.Lock)`
  margin-left: 8px;
  margin-bottom: 2px;
`;

S.Comments = styled.div`
  margin: 0 auto 80px;
  max-width: 1440px;
`;

S.Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 0;
`;

S.ShareOptionsWrap = styled.div`
  margin: 10px 0 0 0;
  @media ${u.device.tablet} {
    margin: 25px 0 0 0;
  }
`;

S.IconWrap = styled.div`
  margin: 25px 0 15px 0;
  > * {
    margin: 0 25px 0 0;
  }
`;

S.Twitter = styled(I.Twitter)`
  cursor: pointer;
`;
S.Facebook = styled(I.Facebook)`
  cursor: pointer;
`;
S.LinkedIn = styled(I.LinkedIn)`
  cursor: pointer;
`;
S.Hyperlink = styled(I.Hyperlink)`
  cursor: pointer;
`;

S.EventContainer = styled.div`
  background-color: white;
  padding: 20px 16px;

  @media ${u.device.mobile} {
    padding: 20px;
  }
  @media ${u.device.tablet} {
    padding: 40px;
  }
  @media ${u.device.desktop} {
    display: flex;
    flex-direction: row-reverse;
    justify-content: space-between;
  }
`;

S.Video = styled.div`
  border-radius: 3px 3px 0 0;
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  overflow: hidden;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

S.Image = styled.div`
  height: 211px;
  margin: 0 auto;
  min-width: 268px;
  width: 100%;
  position: relative;

  @media ${u.device.tablet} {
    height: 320px;
  }
`;

S.BgImage = styled(Image)`
  filter: brightness(1.2) blur(8px);
`;

S.Content = styled.div`
  word-wrap: break-word;
  position: relative;

  @media ${u.device.desktop} {
    margin-right: 40px;
  }
`;

S.Name = styled.div``;

S.Header = styled.header`
  grid-row: 1 / 3;
  grid-column: 2 / 3;
`;

S.Details = styled.div`
  align-self: flex-start;
  display: flex;
  flex-direction: column;
  @media ${u.device.desktop} {
    width: 273px;
  }
`;

S.Location = styled.div`
  align-items: center;
  display: flex;
  margin-bottom: 40px;
  > a {
    margin: 0 0 0 60px;
  }
`;

S.Duration = styled.div`
  display: flex;
  margin-bottom: 30px;
`;

S.Clock = styled(FaClock)`
  color: ${u.colours.teal};
  margin-right: 5px;
  min-width: 20px;
  min-height: 20px;
`;

S.MapIcon = styled(FaMapMarkerAlt)`
  color: ${u.colours.teal};
  margin-right: 5px;
  min-width: 20px;
  min-height: 20px;
`;

S.Info = styled.div`
  @media ${u.device.desktop} {
    grid-column-start: 1;
    grid-column-end: 2;
  }
`;

S.Button = styled(Button)`
  margin: auto;
`;

const TT = {};

TT.H3 = styled(T.H3)`
  margin: 20px 0;
`;

TT.H3Rox = styled(T.H3Rox)`
  margin-bottom: 20px;
  @media ${u.device.mobile} {
    font-size: 3.2rem;
    line-height: 3.8rem;
  }
`;

TT.CL = styled(T.CL)`
  color: ${u.colours.charcoal};
  margin-bottom: 20px;
  ${(p) => p.isError && "color: red;"}
  @media ${u.device.desktop} {
    margin: 20px 0 0;
  }
`;

TT.CMA = styled(T.CL)`
  margin-bottom: 20px;
  color: ${u.colours.charcoal};
`;

TT.CMC = styled(T.CL)`
  color: ${u.colours.charcoal};
  margin-top: 10px;
  margin-right: 10px;
`;

TT.CS = styled(T.CS)`
  color: ${u.colours.charcoalMid};
  font-weight: 700;
  margin-bottom: 10px;
`;

TT.CLA = styled(T.CL)`
  line-height: 2.6rem;
  margin-top: 20px;
`;

TT.CLC = styled(T.CL)`
  color: white;
  margin-bottom: 10px;
  text-align: center;
`;

S.ModalContent = styled.div`
  padding: 5% 0;
`;

S.ModalCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 0 3rem 2rem 3rem;
`;
